/**
 * How often something repeats — a recurring job, a location's service
 * (docs/majik/DATA-ARCHITECTURE-SPEC.md → `cadence`). One definition, read by
 * every screen that says "Weekly" or "Every other week".
 */
export const cadences = [
  'daily',
  'weekly',
  'biweekly',
  'every_3_weeks',
  'every_4_weeks',
  'monthly',
  'quarterly',
] as const
export type Cadence = (typeof cadences)[number]

export const cadenceLabels: Record<Cadence, string> = {
  daily: 'Every day',
  weekly: 'Weekly',
  biweekly: 'Every other week',
  every_3_weeks: 'Every 3 weeks',
  every_4_weeks: 'Every 4 weeks',
  monthly: 'Monthly',
  quarterly: 'Every 3 months',
}

/** "Every Tuesday", "Every other Wednesday" — a rule's day, in words. */
export function cadenceOnDay(cadence: Cadence, weekday: number): string {
  const day = weekdayNames[weekday] ?? ''
  switch (cadence) {
    case 'weekly':
      return `Every ${day}`
    case 'biweekly':
      return `Every other ${day}`
    case 'every_3_weeks':
      return `Every 3 weeks on ${day}`
    case 'every_4_weeks':
      return `Every 4 weeks on ${day}`
    default:
      return cadenceLabels[cadence]
  }
}

/** Sunday first, matching `Date.getUTCDay()`. */
export const weekdayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

/** Monday first — the order Majik's week is shown in (service tiles, schedules). */
export const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type WeekdayKey = (typeof weekdayKeys)[number]
